import { GenericStyleModel } from '../../Styles/models/generic-style.model';
import { RichTextEditorDefaultStylesModel } from '../../Forms/models/rich-text-editor-default-styles.model';
import { FontAlignmentEnum } from '../../Forms/enums/font-alignment.enum';
import { StyleStateEnum } from '../enums/style-state.enum';
import { DefaultFontsEnum } from '../enums/default-fonts.enum';
import { PaddingModel } from './padding.model';
import { isBooleanEqual, isObjectEqual } from '../../Misc/helpers/operator/operator';

export const defaultFontFamily: string = 'Arial, Helvetica, sans-serif';

export class TextStyleModel extends GenericStyleModel {
    declare styleState: number | string;
    declare displayStyleState: number | string;
    color: string;
    fontFamily: string;
    fontSize: string;
    bold: boolean;
    italics: boolean = false;
    underlined: boolean = false;
    alignment: FontAlignmentEnum;
    backgroundColor: string;
    padding: PaddingModel;

    constructor(apiModel?: any) {
        super(apiModel);
        if (apiModel) {
            this.setFromApiModel(apiModel);
        }

        if (!this.fontFamily) {
            this.fontFamily = defaultFontFamily;
        }
        if (!this.styleState) {
            this.styleState = StyleStateEnum.DEFAULT;
        }

        if (!this.displayStyleState) {
            this.displayStyleState = this.styleState;
        }

        if (!this.backgroundColor) {
            this.backgroundColor = '#FFFFFF';
        }

        if (!this.padding) {
            this.padding = new PaddingModel();
        }
    }

    clone(): TextStyleModel {
        const clone = new TextStyleModel(
            {
                color: this.color,
                fontFamily: this.fontFamily,
                fontSize: this.fontSize,
                bold: this.bold,
                italics: this.italics,
                underlined: this.underlined,
                alignment: this.alignment,
                styleState: this.styleState,
                displayStyleState: this.displayStyleState,
                backgroundColor: this.backgroundColor,
                padding: this.padding
            }
        );

        if (this.template) {
            clone.template = this.template.clone();
        }

        return clone;
    }

    equals(other: TextStyleModel, compareTemplate: boolean): boolean {

        if (!other) {
            return false;
        }

        // If they're both saved styles we don't need to look further
        // @HACK This if condition is complicated because clone isn't used in all places, some use JSON.parse(JSON.stringify(, but it's complicated to update
        // that so for now if other doesn't have isSavedStyle, just check to see that other.styleState is a number, which is equivalent
        if (
            this.isSavedStyle() &&
            (
                (
                    other.hasOwnProperty('isSavedStyle') &&
                    other.isSavedStyle()
                ) ||
                !!Number(other.styleState)
            )
        ) {
            return this.styleState === other.styleState;
        }

        if (!super.equals(other, compareTemplate)) {
            return false;
        }

        if (this.color !== other.color) {
            return false;
        }

        if (this.fontFamily !== other.fontFamily) {
            return false;
        }

        if (!isBooleanEqual(this.bold, other.bold)) {
            return false;
        }

        if (this.italics !== other.italics) {
            return false;
        }

        if (!isBooleanEqual(this.underlined, other.underlined)) {
            return false;
        }

        if (!isObjectEqual(this.alignment, other.alignment)) {
            return false;
        }

        if (this.backgroundColor !== other.backgroundColor) {
            return false;
        }

        if (this.fontSize !== other.fontSize) {
            return false;
        }

        if (!this.padding.equals(other.padding)){
            return false;
        }

        return true;
    }

    setFromApiModel(apiModel: any): void {
        super.setFromApiModel(apiModel);

        if (typeof apiModel === 'string') {
            apiModel = JSON.parse(apiModel);
        }

        if (apiModel.hasOwnProperty('color')) {
            this.color = apiModel.color;
        }
        if (apiModel.hasOwnProperty('fontFamily')) {
            this.fontFamily = apiModel.fontFamily;
        }

        if (this.fontFamily === DefaultFontsEnum.backwardCompatibilityArial) {
            this.fontFamily = DefaultFontsEnum.Arial;
        } else if (this.fontFamily === DefaultFontsEnum.backwardCompatibilityHelvetica) {
            this.fontFamily = DefaultFontsEnum.Helvetica;
        }

        if (apiModel.hasOwnProperty('fontSize')) {
            this.fontSize = apiModel.fontSize;
        }
        if (apiModel.hasOwnProperty('bold')) {
            this.bold = apiModel.bold;
        }
        if (apiModel.hasOwnProperty('italics')) {
            this.italics = apiModel.italics;
        }
        if (apiModel.hasOwnProperty('underlined')) {
            this.underlined = apiModel.underlined;
        }
        if (apiModel.hasOwnProperty('alignment')) {
            this.alignment = apiModel.alignment;
        }
        if (apiModel.hasOwnProperty('backgroundColor')) {
            this.backgroundColor = apiModel.backgroundColor;
        }
        if (apiModel.hasOwnProperty('padding')) {
            this.padding = new PaddingModel(apiModel.padding.paddingAll, apiModel.padding.paddingOption, apiModel.padding.topPadding,
                apiModel.padding.leftPadding, apiModel.padding.bottomPadding, apiModel.padding.rightPadding);
        }
    }

    toApi(): any {
        return {
            color: this.color,
            fontFamily: this.fontFamily,
            fontSize: this.fontSize,
            bold: this.bold,
            italics: this.italics,
            underlined: this.underlined,
            alignment: this.alignment,
            defaultReference: this.defaultReference ? this.defaultReference : StyleStateEnum.DEFAULT,
            backgroundColor: this.backgroundColor,
            padding: this.padding
        };
    }

    protected _toBlockStyleModel(): any {
        return {
            color: this.color,
            fontFamily: this.fontFamily,
            fontSize: this.fontSize,
            bold: this.bold,
            italics: this.italics,
            underlined: this.underlined,
            alignment: this.alignment,
            styleState: this.styleState,
            displayStyleState: this.displayStyleState,
            backgroundColor: this.backgroundColor,
            padding: this.padding
        };
    }

    toBlockStyle(): any {
        if (!this.isSavedStyle()) {
            return this._toBlockStyleModel();
        } else {
            return {
                styleState: this.styleState
            };
        }
    }

    toTextEditorStyles(): RichTextEditorDefaultStylesModel {
        const blockStyle = this._toBlockStyleModel();
        delete blockStyle.styleState;
        delete blockStyle.displayStyleState;

        const result = {};
        for (const formatOptionKey in blockStyle) {
            if (!blockStyle.hasOwnProperty(formatOptionKey)) {
                continue;
            }

            const styleValue = blockStyle[formatOptionKey];

            if (formatOptionKey === 'fontFamily') {
                result['font-family'] = styleValue;

                continue;
            }

            if (formatOptionKey === 'italics' && styleValue) {
                result['font-style'] = 'italic';

                continue;
            }

            if (formatOptionKey === 'bold' && styleValue) {
                result['font-weight'] = 'bold';
                continue;
            }

            if (formatOptionKey === 'underlined' && styleValue) {
                result['text-decoration'] = 'underline';

                continue;
            }

            if (formatOptionKey === 'alignment') {
                result['text-align'] = styleValue;

                continue;
            }

            if (formatOptionKey === 'backgroundColor') {
                result['background-color'] = styleValue;

                continue;
            }

            if (formatOptionKey === 'padding') {
                if (styleValue['paddingOption']){
                    result['padding-top'] = styleValue['topPadding'];
                    result['padding-left'] = styleValue['leftPadding'];
                    result['padding-bottom'] = styleValue['bottomPadding'];
                    result['padding-right'] = styleValue['rightPadding'];
                }else{
                    result['padding-top'] = styleValue['paddingAll'];
                    result['padding-left'] = styleValue['paddingAll'];
                    result['padding-bottom'] = styleValue['paddingAll'];
                    result['padding-right'] = styleValue['paddingAll'];
                }
                continue;
            }

            result[formatOptionKey] = styleValue;
        }

        return <RichTextEditorDefaultStylesModel>result;
    }
}
